import { useMemo } from "react"
import type { Condition, ConditionGroup, NextPageParams } from "@/schema"
import type { Project } from "@/schema/project"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  fetchBusinessProjects,
  fetchContactProjects,
  fetchHouseholdProjects,
  fetchHouseholdRecordOptions,
  fetchPaginatedProjects,
  fetchProject,
  fetchProjects,
  PaginatedProjectsResponse,
} from "./api"

interface InfiniteProjectsParams {
  searchTerm?: string
  filters?: Condition[]
  viewSort?: { field?: string; direction?: "asc" | "desc" }
  includeInactive?: boolean
  projectTemplateId?: string
  enabled?: boolean
  bookIdOverride?: string
  withoutHousehold?: boolean
  withoutProjectable?: boolean
  projectableType?: "Household" | "Contact" | "Business"
  projectableId?: string
}

interface InfiniteProjectsData {
  projects: Project[]
  milestoneCounts: Record<string, number>
}

const NEXT_MEETING_DEADLINE_REFRESH_INTERVAL = 60_000

function hasNextMeetingDeadlineReference(project?: Project): boolean {
  return (
    project?.project_template.project_milestones.some((milestone) =>
      milestone.task_templates?.some((taskTemplate) => taskTemplate.deadline_reference === "next_meeting_date")
    ) ?? false
  )
}

function useInfiniteProjectsInternal({
  searchTerm = "",
  filters = [],
  viewSort,
  includeInactive = false,
  projectTemplateId,
  enabled = true,
  bookIdOverride,
  withoutHousehold = false,
  withoutProjectable = false,
  projectableType,
  projectableId,
}: InfiniteProjectsParams) {
  const getContext = useApiContext(bookIdOverride)
  const routeBookId = useBookId()
  const resolvedBookId = bookIdOverride ?? routeBookId

  const filterGroup = useMemo<ConditionGroup>(
    () => ({
      combinator: "and",
      conditions: filters,
    }),
    [filters]
  )

  const filtersKey = useMemo(() => JSON.stringify(filterGroup.conditions ?? []), [filterGroup])
  const sortKey = viewSort ? `${viewSort.field ?? ""}:${viewSort.direction ?? ""}` : ""

  return useInfiniteQuery<
    PaginatedProjectsResponse,
    Error,
    InfiniteProjectsData,
    [
      string,
      string,
      string,
      string,
      string,
      boolean,
      string | undefined,
      string | undefined,
      boolean,
      boolean,
      string | undefined,
      string | undefined,
    ],
    NextPageParams | undefined
  >({
    queryKey: [
      "projects",
      "infinite",
      searchTerm,
      filtersKey,
      sortKey,
      includeInactive,
      projectTemplateId,
      resolvedBookId,
      withoutHousehold,
      withoutProjectable,
      projectableType,
      projectableId,
    ],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPaginatedProjects(
        context,
        searchTerm,
        pageParam,
        filterGroup,
        viewSort,
        includeInactive,
        projectTemplateId,
        withoutHousehold,
        withoutProjectable,
        projectableType,
        projectableId
      )
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      projects: data.pages.flatMap((page) => page.projects),
      milestoneCounts: data.pages[0]?.milestone_counts ?? {},
    }),
  })
}

export function useProjects() {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProjects(context)
    },
  })
}

export function useProject(id: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProject(context, id)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    refetchInterval: (query) =>
      hasNextMeetingDeadlineReference(query.state.data) ? NEXT_MEETING_DEADLINE_REFRESH_INTERVAL : false,
    refetchIntervalInBackground: false,
  })
}

export function useInfiniteProjects(enabled?: boolean, bookIdOverride?: string) {
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  return useInfiniteProjectsInternal({
    searchTerm,
    filters: completedConditions,
    viewSort,
    enabled,
    bookIdOverride,
  })
}

export function useInfiniteProjectsWithFilters(params: InfiniteProjectsParams) {
  return useInfiniteProjectsInternal(params)
}

export function useProjectsByTemplateId(
  templateId: string,
  options?: { enabled?: boolean; includeInactive?: boolean }
) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["project-template", templateId, "projects", options?.includeInactive || false],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const params = new URLSearchParams({ project_template_id: templateId })
      if (options?.includeInactive) {
        params.append("include_inactive", "true")
      }
      return fetchProjects(context, params.toString())
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!templateId,
  })
}

export function useInfiniteProjectsByTemplateId(
  templateId: string,
  options?: {
    enabled?: boolean
    includeInactive?: boolean
    searchTerm?: string
    filters?: Condition[]
    bookIdOverride?: string
  }
) {
  return useInfiniteProjectsInternal({
    searchTerm: options?.searchTerm,
    filters: options?.filters,
    projectTemplateId: templateId,
    includeInactive: options?.includeInactive,
    enabled: options?.enabled !== undefined ? options.enabled : !!templateId,
    bookIdOverride: options?.bookIdOverride,
  })
}

export function useHouseholdRecordOptions(
  householdId: string | null | undefined,
  formFieldId: string,
  enabled = true,
  bookIdOverride?: string
) {
  const getContext = useApiContext(bookIdOverride)
  return useQuery({
    queryKey: ["household", householdId, "household-record-options", formFieldId, bookIdOverride ?? null],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdRecordOptions(context, householdId!, formFieldId)
    },
    enabled: enabled && !!householdId && !!formFieldId,
    staleTime: 30_000,
  })
}

export function useFetchHouseholdProjects(householdId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["household", householdId, "projects"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdProjects(context, householdId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!householdId,
  })
}

export function useFetchContactProjects(contactId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["contact", contactId, "projects"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContactProjects(context, contactId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!contactId,
  })
}

export function useFetchBusinessProjects(businessId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["business", businessId, "projects"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinessProjects(context, businessId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!businessId,
  })
}
